<template>
  <v-dialog
    v-model="dialogOpen"
    max-width="600px"
    persistent
  >
    <v-card class="linking-dialog-card">
      <v-card-title>
        <span class="text-h5">{{ $t('$vuetify.floorPlan.linkTableToObject') }}</span>
      </v-card-title>

      <!-- Fiksni gornji dio -->
      <v-card-text class="linking-dialog-header pb-2">
        <div class="text-caption grey--text mb-2">
          {{ $t('$vuetify.floorPlan.canvasObject') }}: <strong class="black--text">{{ tableObject && tableObject.tableName }}</strong>
        </div>
        <v-row dense class="align-center">
          <v-col>
            <v-text-field
              v-model="search"
              :label="$t('$vuetify.floorPlan.searchTables')"
              prepend-inner-icon="mdi-magnify"
              clearable
              outlined
              dense
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="auto">
            <v-checkbox
              v-model="showLinkedTables"
              :label="$t('$vuetify.floorPlan.showLinkedTables')"
              dense
              hide-details
              class="mt-0 pt-0"
            ></v-checkbox>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider></v-divider>

      <!-- Scrollabilna lista -->
      <v-card-text class="linking-dialog-content pa-0">
        <v-list dense>
          <v-list-item-group v-model="selectedTable" color="primary">
            <v-list-item
              v-for="table in filteredTables"
              :key="table.id"
              :value="table.id"
            >
              <v-list-item-icon>
                <v-icon :color="getTableStatusColor(table)">
                  {{ table.locked_by ? 'mdi-lock' : 'mdi-table-furniture' }}
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ table.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ getTableStatusText(table) }}
                  <span v-if="table.items && table.items.length > 0">
                    - {{ table.items.length }} {{ $t('$vuetify.floorPlan.items') }}
                  </span>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-chip
                  v-if="isTableLinked(table.id)"
                  x-small
                  color="warning"
                  dark
                >
                  {{ $t('$vuetify.floorPlan.alreadyLinked') }}
                </v-chip>
              </v-list-item-action>
            </v-list-item>
          </v-list-item-group>

          <v-list-item v-if="filteredTables.length === 0">
            <v-list-item-content>
              <v-list-item-title class="text-center grey--text">
                {{ $t('$vuetify.floorPlan.noTablesFound') }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>

        <v-alert
          v-if="selectedTableData"
          dense
          text
          type="info"
          class="mx-3 mb-3"
        >
          <div class="text-caption">{{ $t('$vuetify.floorPlan.linkingInfo') }}</div>
        </v-alert>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions>
        <v-btn
          color="success"
          text
          @click="openAddTableDialog"
        >
          <v-icon left>mdi-plus</v-icon>
          {{ $t('$vuetify.tables.create') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="grey"
          text
          @click="close"
        >
          {{ $t('$vuetify.cancel') }}
        </v-btn>
        <v-btn
          v-if="tableObject && tableObject.tableId"
          color="warning"
          text
          @click="unlinkTable"
        >
          {{ $t('$vuetify.floorPlan.unlinkTable') }}
        </v-btn>
        <v-btn
          color="primary"
          text
          @click="linkTable"
          :disabled="!selectedTable"
        >
          {{ $t('$vuetify.floorPlan.link') }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Add New Table Dialog -->
    <add-new-table-dialog
      v-if="addTableDialogOpen"
      :table="newTable"
      :tables="availableTables"
      @closeAddTableDialog="addTableDialogOpen = false"
      @addTable="onAddTable"
    ></add-new-table-dialog>
  </v-dialog>
</template>

<script>
import AddNewTableDialog from '@/modules/cash-register/components/dialogs/AddNewTableDialog.vue'

export default {
  name: 'TableLinkingDialog',
  components: {
    AddNewTableDialog
  },
  props: {
    tableObject: {
      type: Object,
      default: null
    },
    availableTables: {
      type: Array,
      default: () => []
    },
    linkedTableIds: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      dialogOpen: false,
      selectedTable: null,
      search: '',
      addTableDialogOpen: false,
      newTable: {},
      showLinkedTables: false
    }
  },
  computed: {
    filteredTables () {
      let tables = this.availableTables

      // Filter by search
      if (this.search) {
        const searchLower = this.search.toLowerCase()
        tables = tables.filter(table =>
          table.name.toLowerCase().includes(searchLower)
        )
      }

      // Filter out linked tables if showLinkedTables is false
      if (!this.showLinkedTables) {
        tables = tables.filter(table => !this.isTableLinked(table.id))
      }

      return tables
    },
    selectedTableData () {
      if (!this.selectedTable) return null
      return this.availableTables.find(t => t.id === this.selectedTable)
    }
  },
  watch: {
    tableObject: {
      immediate: true,
      handler (newVal) {
        if (newVal && newVal.tableId) {
          this.selectedTable = newVal.tableId
        } else {
          this.selectedTable = null
        }
      }
    }
  },
  methods: {
    open () {
      this.dialogOpen = true
      if (this.tableObject && this.tableObject.tableId) {
        this.selectedTable = this.tableObject.tableId
      }
    },
    close () {
      this.dialogOpen = false
      this.selectedTable = null
      this.search = ''
      this.showLinkedTables = false
    },
    linkTable () {
      if (!this.selectedTable) return

      this.$emit('link-table', {
        canvasObjectId: this.tableObject.id,
        tableId: this.selectedTable
      })

      this.close()
    },
    unlinkTable () {
      this.$emit('unlink-table', this.tableObject.id)
      this.close()
    },
    isTableLinked (tableId) {
      return this.linkedTableIds.includes(tableId)
    },
    getTableStatusColor (table) {
      if (table.locked_by) return 'grey'
      if (table.items && table.items.length > 0) return 'purple'
      return 'blue'
    },
    getTableStatusText (table) {
      if (table.locked_by) return this.$t('$vuetify.floorPlan.locked')
      if (table.items && table.items.length > 0) return this.$t('$vuetify.floorPlan.occupied')
      return this.$t('$vuetify.floorPlan.available')
    },
    openAddTableDialog () {
      // Get next table number
      const nextNumber = this.getNextTableNumber()
      const name = `Stol ${nextNumber}`
      this.newTable = {
        name: name,
        number: nextNumber,
        items: [],
        persistent: true,
        color: 'primary'
      }
      this.addTableDialogOpen = true
    },
    getNextTableNumber () {
      if (this.availableTables.length === 0) {
        return 1
      }
      return Math.max(...this.availableTables.map(table => table.number)) + 1
    },
    onAddTable (table, persist) {
      this.addTableDialogOpen = false
      this.$emit('add-table', { table, persist, canvasObjectId: this.tableObject.id })
      this.close()
    }
  }
}
</script>

<style scoped>
.linking-dialog-card {
  display: flex !important;
  flex-direction: column !important;
  max-height: 80vh !important;
}

.linking-dialog-header {
  flex-shrink: 0;
}

.linking-dialog-content {
  flex: 1 !important;
  overflow-y: auto !important;
}
</style>
